import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, Grid, TextField, MenuItem, Table, TableHead, TableRow, TableCell, TableBody, Button, LinearProgress, Stack, Typography, TableContainer, Paper } from '@mui/material';
import { printReport, currencyCol, textCol } from '../../utils/pdfPrint';
import { useTranslation } from 'react-i18next';
import apiService from '../../services/apiServices';
import PageHeader from '../common/PageHeader';
import SendReportEmailDialog from '../common/SendReportEmailDialog';
import { Link } from 'react-router-dom';
import { ReportPage, ExportMenu } from './ReportKit';

export default function LocalPayrollKS() {
  const { t } = useTranslation();
  const [lists, setLists] = useState([]);
  const [listId, setListId] = useState('');
  const [rows, setRows] = useState([]);
  const [rates, setRates] = useState({ employee: 0.05, employer: 0.05 });
  const [ranges, setRanges] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [dept, setDept] = useState('all');
  const [loading, setLoading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => {
    apiService().get('salariesmonthlylists').then((r) => { if (r?.success) setLists(r.data || []); });
    apiService().get('workers').then((r) => { if (r?.success) setWorkers(r.data || []); });
    apiService().get('department').then((r) => { if (r?.success) setDepartments(r.data || []); });
  }, []);

  useEffect(() => {
    if (!listId) return setRows([]);
    setLoading(true);
    apiService().query('getSalariesByListId', `id=${listId}`).then((r) => {
      if (r?.success) setRows(r.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
    // fetch fiscal politics to compute if fields mungojnë
    apiService().get('fiscalPolitics').then((r) => {
      const fp = r?.success && r.data?.length ? r.data[0] : null;
      if (fp) setRates({ employee: Number(fp.EmployeeTax || 5) / 100, employer: Number(fp.EmployerTax || 5) / 100 });
      if (fp?.Id) {
        apiService().query('getFiscalpoliticsDetails', `FiscalPoliticsId=${fp.Id}`).then((d) => {
          if (d?.success) setRanges(d.data || []);
        });
      }
    });
  }, [listId]);

  const computeTax = useCallback((taxable) => {
    let total = 0;
    for (const r of ranges || []) {
      const from = Number(r.From ?? 0); const to = r.To == null ? null : Number(r.To);
      const rate = Number(r.Tax || 0) / 100;
      if (to == null) { if (taxable > from) total += (taxable - from) * rate; }
      else if (taxable > to) total += (to - from) * rate; else if (taxable > from) total += (taxable - from) * rate;
    }
    return total;
  }, [ranges]);

  const deptMap = useMemo(() => {
    const m = {};
    departments.forEach(d => { m[Number(d.Id)] = d.Name; });
    return m;
  }, [departments]);

  const enrich = useMemo(() => {
    return (rows || []).map((r) => {
      const salary = Number(r.Salary)||0; const hour = Number(r.HourSalary)||0; const bonus = Number(r.Bonus)||0; const pen = Number(r.Penalties)||0;
      const bruto = salary + hour + bonus - pen;
      const empPens = +(rates.employee * salary).toFixed(2);
      const emprPens = +(rates.employer * salary).toFixed(2);
      const tax = (r.taxes && Number(r.taxes)>0) ? Number(r.taxes) : +computeTax(Math.max(salary - empPens,0)).toFixed(2);
      const contrib = (r.contributions && Number(r.contributions)>0) ? Number(r.contributions) : +(empPens + emprPens).toFixed(2);
      const net = (r.neto && Number(r.neto)>0) ? Number(r.neto) : +(salary - empPens - tax + hour + bonus - pen).toFixed(2);
      const deptName = r.DepartmentName || deptMap[Number(r.DepartmentId)] || '—';
      return { ...r, bruto, taxes: tax, contributions: contrib, neto: net, empPens, emprPens, DepartmentName: deptName };
    });
  }, [rows, rates, deptMap, computeTax]);

  const filtered = useMemo(()=> enrich.filter((r)=> dept==='all' || r.DepartmentName===dept), [enrich, dept]);

  const totals = useMemo(() => {
    const sum = (k) => filtered.reduce((a, b) => a + (Number(b[k]) || 0), 0);
    return { gross: sum('bruto'), emp: sum('empPens'), empr: sum('emprPens'), contributions: sum('contributions'), taxes: sum('taxes'), neto: sum('neto'), cost: sum('neto') + sum('taxes') + sum('contributions') };
  }, [filtered]);

  function fmt(n) {
    return new Intl.NumberFormat('sq-AL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);
  }

  const fullName = (r) => r?.FullName || r?.fullName || `${r?.FirstName || r?.firstname || ''} ${r?.LastName || r?.lastname || ''}`.trim();

  function exportCsv() {
    const headers = [t('report.name'), t('report.name'), t('report.department'), t('report.gross'), t('report.tax'), t('report.employeeContrib'), t('report.employerCost'), t('report.net'), t('report.totalCost')];
    const lines = [headers.join(',')];
    filtered.forEach((r) => {
      const bruto = (Number(r.Salary)||0) + (Number(r.HourSalary)||0) + (Number(r.Bonus)||0) - (Number(r.Penalties)||0);
      const [fn, ln] = fullName(r).split(' ');
      const cost = (r.neto||0) + (r.taxes||0) + (r.contributions||0);
      const line = [fn||'', ln||'', r.DepartmentName||'-', bruto, r.taxes||0, r.empPens||0, r.emprPens||0, r.neto||0, cost]
        .map((x) => `"${String(x).replace(/"/g,'""')}"`).join(',');
      lines.push(line);
    });
    const csv = lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'payroll_ks.csv'; a.click(); URL.revokeObjectURL(url);
  }

  function exportPdf() {
    const selectedListLabel = lists.find((l) => String(l.id) === String(listId));
    const period = selectedListLabel ? `${selectedListLabel.month}/${selectedListLabel.year}` : '';
    // Pre-compute totalCost field for each row
    const pdfRows = filtered.map((r) => ({ ...r, _totalCost: r.neto + r.taxes + r.contributions, _name: fullName(r), _dept: r.DepartmentName || '—' }));
    printReport({
      title: t('reports.payroll'),
      subtitle: period ? `${t('report.period')}: ${period}` : '',
      orientation: 'landscape',
      columns: [
        { ...textCol(t('report.worker'), '_name', '22%') },
        { ...textCol(t('report.department'), '_dept', '14%') },
        { ...currencyCol(t('report.gross'), 'bruto', '12%') },
        { ...currencyCol(t('report.contributions'), 'contributions', '12%') },
        { ...currencyCol(t('report.tax'), 'taxes', '11%') },
        { ...currencyCol(t('report.net'), 'neto', '12%') },
        { ...currencyCol(t('report.totalCost'), '_totalCost', '13%') },
      ],
      rows: pdfRows,
      totals: { bruto: totals.gross, contributions: totals.emp + totals.empr, taxes: totals.taxes, neto: totals.neto, _totalCost: totals.cost },
      period,
      badge: { label: `${filtered.length} ${t('report.workers')}` },
    });
  }

  return (
    <ReportPage>
      <PageHeader
        title={t('reports.payroll')}
        actions={<Button component={Link} to="/home/reports" variant="outlined">{t('report.backLabel')}</Button>}
      />
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={4}>
              <TextField select fullWidth label={t('report.selectList')} size="small" value={listId} onChange={(e) => setListId(e.target.value)}>
                <MenuItem value="">{t('report.selectListPlaceholder')}</MenuItem>
                {(lists || []).map((l) => (
                  <MenuItem key={l.id} value={l.id}>{`${l.month}/${l.year}`}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField select fullWidth label={t('report.department')} size="small" value={dept} onChange={(e) => setDept(e.target.value)}>
                <MenuItem value="all">{t('report.allDepartments')}</MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d.Id} value={d.Name}>{d.Name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm="auto">
              <ExportMenu onCsv={exportCsv} onExcel={() => {
                const headers = [t('report.worker'), t('report.department'), t('report.gross'), t('report.tax'), t('report.contributions'), t('report.contributions'), t('report.net'), t('report.totalCost')];
                const rowsHtml = filtered.map((r) => `<tr><td>${fullName(r)}</td><td>${r.DepartmentName || '-'}</td><td>${r.bruto}</td><td>${r.taxes}</td><td>${r.empPens}</td><td>${r.emprPens}</td><td>${r.neto}</td><td>${(r.neto + r.taxes + r.contributions)}</td></tr>`).join('');
                const html = `<!DOCTYPE html><html><head><meta charset='UTF-8'></head><body><table border='1'><thead><tr>${headers.map((h) => `<th>${h}</th>`).join('')}</tr></thead><tbody>${rowsHtml}</tbody></table></body></html>`;
                const blob = new Blob([html], { type: 'application/vnd.ms-excel' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'payroll_ks.xls'; a.click(); URL.revokeObjectURL(url);
              }} onPdf={exportPdf} onEmail={() => setEmailOpen(true)} disabled={!listId} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {listId && filtered.length > 0 && (
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          {[
          { label: t('report.workers'), value: filtered.length },
            { label: t('report.totalBruto'), value: fmt(totals.gross) },
            { label: t('report.totalNeto'), value: fmt(totals.neto) },
            { label: t('report.totalCost'), value: fmt(totals.cost) },
          ].map((item) => (
            <Card key={item.label} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', flex: 1 }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{item.value}</Typography>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <TableContainer component={Paper} elevation={0}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>{t('report.worker')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('report.department')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{t('report.gross')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{t('report.contributions')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{t('report.tax')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{t('report.net')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{t('report.totalCost')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {listId ? t('report.noDataForPeriod') : t('report.selectList')}
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((r) => (
                <TableRow key={r.Id || r.WorkerId} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{fullName(r)}</TableCell>
                  <TableCell>{r.DepartmentName || '—'}</TableCell>
                  <TableCell align="right">{fmt(r.bruto)}</TableCell>
                  <TableCell align="right">{fmt(r.contributions)}</TableCell>
                  <TableCell align="right">{fmt(r.taxes)}</TableCell>
                  <TableCell align="right">{fmt(r.neto)}</TableCell>
                  <TableCell align="right">{fmt(r.neto + r.taxes + r.contributions)}</TableCell>
                </TableRow>
              ))}
              {filtered.length > 0 && (
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 700 }}>{t('report.total')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(totals.gross)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(totals.emp + totals.empr)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(totals.taxes)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(totals.neto)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(totals.cost)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <SendReportEmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        reportTitle={t('reports.payroll')}
        subtitle={lists.find(l => String(l.id) === String(listId)) ? `${lists.find(l => String(l.id) === String(listId))?.month}/${lists.find(l => String(l.id) === String(listId))?.year}` : ''}
        columns={[
          { label: t('report.worker'), key: r => fullName(r), align: 'left' },
          { label: t('report.department'), key: 'DepartmentName', align: 'left' },
          { label: t('report.gross'), key: 'bruto', align: 'right', format: 'currency' },
          { label: t('report.contributions'), key: 'contributions', align: 'right', format: 'currency' },
          { label: t('report.tax'), key: 'taxes', align: 'right', format: 'currency' },
          { label: t('report.net'), key: 'neto', align: 'right', format: 'currency' },
        ]}
        rows={filtered}
        totals={{ bruto: totals.gross, contributions: totals.emp + totals.empr, taxes: totals.taxes, neto: totals.neto }}
        workers={workers}
      />
    </ReportPage>
  );
}
